import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { Button, Card, CardContent } from "@devhop/ui";
import { useNavigate } from "@tanstack/react-router";
import { useCreateLeaveRequest, useLeaveBalanceQuery, useLeaveTypesQuery } from "../api/queries";
import { LEAVE_LIST_SEARCH_DEFAULTS } from "../constants";
import type { LeaveRequestFormValues } from "../ui/LeaveRequestForm";
import { LeaveRequestForm } from "../ui/LeaveRequestForm";
import { ArrowLeft, HelpCircle, Info } from "lucide-react";
import { HrTour } from "../tour/HrTour";
import { useHrTour } from "../tour/useHrTour";
import { leaveRequestFormTourSteps } from "../tour/hrTourSteps";
import { getLeaveRequestCreateFormSamples } from "../ui/leaveRequestCreateFormSamples";

export function LeaveRequestNewPage() {
  const navigate = useNavigate({ from: "/app/hr/leave/requests/new" });
  const leaveTypes = useLeaveTypesQuery();
  const balance = useLeaveBalanceQuery();
  const createLeave = useCreateLeaveRequest();
  const tour = useHrTour("hr-leave-form-tour-completed");

  const handleSubmit = (values: LeaveRequestFormValues) => {
    createLeave.mutate(
      {
        leaveTypeId: values.leaveTypeId,
        startDate: values.startDate,
        endDate: values.endDate,
        reason: values.reason ?? undefined,
        attachmentKey: values.attachmentKey ?? undefined,
      },
      {
        onSuccess: () => {
          navigate({
            to: "/app/hr/leave/requests",
            search: LEAVE_LIST_SEARCH_DEFAULTS,
          });
        },
      },
    );
  };

  return (
    <>
      <Header />
      <Main>
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="">
            <div className="flex items-center gap-2">
              <h1 className="font-bold text-2xl tracking-tight">ສ້າງຄຳຮ້ອງລາໃໝ່</h1>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="size-8"
                onClick={tour.startTour}
                aria-label="ເລີ່ມທົດສອບການນຳທາງ"
                title="ເລີ່ມທົດສອບການນຳທາງ"
              >
                <HelpCircle className="size-4" />
              </Button>
            </div>
            <p className="mt-1 text-muted-foreground">
              ສ້າງຄຳຮ້ອງລາໃໝ່ — ກະລຸນາເລືອກປະເພດລາ ແລະ ວັນທີ
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => navigate({ to: "/app/hr/leave/requests", search: LEAVE_LIST_SEARCH_DEFAULTS })}>
              <ArrowLeft className="mr-1 size-4" />
              ກັບຄືນ
            </Button>
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <LeaveRequestForm
              leaveTypes={leaveTypes.data ?? []}
              onSubmit={handleSubmit}
              submitting={createLeave.isPending}
              submitLabel="ສົ່ງຄຳຮ້ອງ"
              cancelTo="/app/hr/leave/requests"
              stickySubmit
              autoFillGetSamples={getLeaveRequestCreateFormSamples}
            />
          </div>
          <div className="space-y-4">
            <Card>
              <CardContent className="p-4 py-0">
                <h3 className="mb-3 font-semibold">ຍອດຄ້າງລາພັກ</h3>
                <ul className="space-y-2 text-sm">
                  {balance.data?.map((b) => (
                    <li key={b.leaveTypeId} className="flex justify-between">
                      <span>{b.leaveTypeName}</span>
                      <span className="font-medium">{b.remainingDays} ວັນ</span>
                    </li>
                  ))}
                  {(!balance.data || balance.data.length === 0) && (
                    <li className="text-muted-foreground">ຍັງບໍ່ມີຂໍ້ມູນຍອດຄ້າງ</li>
                  )}
                </ul>
              </CardContent>
            </Card>
            <Card className="border-black bg-black text-white">
              <CardContent className="p-4 py-0">
                <div className="flex gap-2">
                  <span className="text-lg"><Info className="size-4" /></span>
                  <div className="text-sm">
                    <p className="font-medium">ຂໍ້ຄວນຈື່ກ່ອນສົ່ງຄຳຮ້ອງ</p>
                    <p className="mt-1 text-white/90">
                      ກະລຸນາສົ່ງຄຳຮ້ອງຢ່າງນ້ອຍ 3 ວັນລ່ວງໜ້າເພື່ອດຳເນີນການ. ການລາເຈັບດ່ວນສາມາດເອກະສານຫຼັງກັບມາໄດ້.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        <HrTour
          steps={leaveRequestFormTourSteps}
          run={tour.run}
          onCallback={tour.handleJoyrideCallback}
        />
      </Main>
    </>
  );
}
